import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import type { ProjectEntry } from '@erp/rpc';
import { useGT } from 'gt-react';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  ImageAttachmentValue,
  Message,
} from '@/context/agent-state.context';
import type { ChatInputAttachment } from '@/components/common/chat/ChatInput';
import { useCan } from '@/hooks/useCan';
import type {
  ProjectFolderCrumb,
  ProjectWorkspaceViewProps,
} from './ProjectWorkspaceView';
import {
  EMPTY_PROJECT_CHAT_STREAM,
  reduceProjectChatStream,
} from './projectChatStreamState';
import { useProjectUploadQueue } from './useProjectUploadQueue';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';

function useWorkspaceNavigation() {
  const [folderStack, setFolderStack] = useState<ProjectFolderCrumb[]>([]);
  const [selected, setSelected] = useState<ProjectEntry | null>(null);
  const [selectedInputIds, setSelectedInputIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [search, setSearch] = useState('');
  const currentFolder = folderStack.at(-1) ?? null;
  const openFolder = (entry: ProjectEntry) => {
    setFolderStack((current) => [
      ...current,
      { id: entry.id, name: entry.name },
    ]);
    setSelected(null);
    setSearch('');
  };
  const toggleInput = (entryId: string) => {
    setSelectedInputIds((current) => {
      const next = new Set(current);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };
  return {
    folderStack,
    setFolderStack,
    selected,
    setSelected,
    selectedInputIds,
    search,
    setSearch,
    currentFolder,
    openFolder,
    toggleInput,
  };
}

function useProjectQueries(
  projectId: string,
  currentFolderId: string | null,
  search: string,
) {
  const project = useQuery(
    orpc.projects.get.queryOptions({ input: { projectId } }),
  );
  const entries = useQuery(
    orpc.projects.entries.list.queryOptions({
      input: { projectId, parentEntryId: currentFolderId },
    }),
  );
  const results = useQuery({
    ...orpc.projects.entries.search.queryOptions({
      input: { projectId, query: search || ' ', limit: 50 },
    }),
    enabled: search.trim().length > 0,
  });
  const changeset = useQuery(
    orpc.projects.changesets.getOpen.queryOptions({ input: { projectId } }),
  );
  const chat = useQuery(
    orpc.projects.chat.history.queryOptions({ input: { projectId } }),
  );
  return {
    project,
    changeset,
    chat,
    visibleEntries: search.trim() ? (results.data ?? []) : (entries.data ?? []),
  };
}

function useProjectMutations(
  projectId: string,
  currentFolderId: string | null,
) {
  const queryClient = useQueryClient();
  const [reviewOpen, setReviewOpen] = useState(false);
  const invalidateEntries = () =>
    queryClient.invalidateQueries({
      queryKey: orpc.projects.entries.list.queryKey({
        input: { projectId, parentEntryId: currentFolderId },
      }),
    });
  const invalidateChangeset = () =>
    queryClient.invalidateQueries({
      queryKey: orpc.projects.changesets.getOpen.queryKey({
        input: { projectId },
      }),
    });
  const createFolder = useMutation(
    orpc.projects.entries.createFolder.mutationOptions({
      onSuccess: invalidateEntries,
    }),
  );
  const publish = useMutation(
    orpc.projects.changesets.publish.mutationOptions({
      onSuccess: async () => {
        setReviewOpen(false);
        await Promise.all([invalidateChangeset(), invalidateEntries()]);
      },
    }),
  );
  const discard = useMutation(
    orpc.projects.changesets.discard.mutationOptions({
      onSuccess: async () => {
        setReviewOpen(false);
        await invalidateChangeset();
      },
    }),
  );
  return {
    queryClient,
    reviewOpen,
    setReviewOpen,
    invalidateEntries,
    invalidateChangeset,
    createFolder,
    publish,
    discard,
  };
}

function useProjectChat(
  projectId: string,
  selectedEntryIds: string[],
  refresh: () => Promise<unknown>,
) {
  const gt = useGT();
  const [message, setMessage] = useState('');
  const [stream, setStream] = useState(EMPTY_PROJECT_CHAT_STREAM);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(
    null,
  );
  const [pendingScreenshotDataUrl, setPendingScreenshotDataUrl] = useState<
    string | undefined
  >();
  const [chatBusy, setChatBusy] = useState(false);
  const chatBusyRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const send = async (screenshot?: ImageAttachmentValue) => {
    const content =
      message.trim() ||
      (screenshot
        ? gt('Please review this screenshot.', {
            context: 'default project Nami prompt for a screenshot-only turn',
          })
        : '');
    if (!content || chatBusyRef.current) return;
    chatBusyRef.current = true;
    setMessage('');
    setStream(EMPTY_PROJECT_CHAT_STREAM);
    setPendingUserMessage(content);
    setPendingScreenshotDataUrl(
      screenshot
        ? `data:${screenshot.mimeType};base64,${screenshot.imageData}`
        : undefined,
    );
    setChatBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const iterator = await rpcClient.projects.chat.send(
        { projectId, content, selectedEntryIds, screenshot },
        { signal: controller.signal },
      );
      for await (const event of iterator) {
        setStream((current) => reduceProjectChatStream(current, event));
      }
    } catch {
      if (!controller.signal.aborted) {
        setStream((current) => ({
          ...current,
          error: gt(
            'The assistant could not complete that request. Please try again.',
            {
              context: 'project Nami unexpected request error',
            },
          ),
        }));
      }
    } finally {
      await refresh().then(
        () => setStream((current) => ({ ...current, streamingText: '' })),
        () => undefined,
      );
      setPendingUserMessage(null);
      setPendingScreenshotDataUrl(undefined);
      setChatBusy(false);
      chatBusyRef.current = false;
      abortRef.current = null;
    }
  };
  const cancel = () => {
    abortRef.current?.abort();
    void rpcClient.projects.chat
      .cancel({ projectId })
      .catch((error: unknown) => {
        console.error('Project cancellation request failed', error);
      });
  };
  return {
    message,
    setMessage,
    streamingText: stream.streamingText,
    error: stream.error,
    activeToolCalls: stream.activeToolCalls,
    pendingUserMessage,
    pendingScreenshotDataUrl,
    chatBusy,
    send,
    cancel,
  };
}

// This hook assembles workspace state and callbacks; its branches are UI wiring.
// fallow-ignore-next-line complexity
export function useProjectWorkspaceController(projectId: string): {
  project: ReturnType<typeof useProjectQueries>['project'];
  viewProps: ProjectWorkspaceViewProps;
} {
  const gt = useGT();
  const uploadInput = useRef<HTMLInputElement>(null);
  const namiUploadInput = useRef<HTMLInputElement>(null);
  const [namiTaskIds, setNamiTaskIds] = useState<string[]>([]);
  const [canCreate] = useCan(Action.Create, Subject.Project);
  const [canExecute] = useCan(Action.Execute, Subject.Project);
  const nav = useWorkspaceNavigation();
  const data = useProjectQueries(
    projectId,
    nav.currentFolder?.id ?? null,
    nav.search,
  );
  const mutations = useProjectMutations(
    projectId,
    nav.currentFolder?.id ?? null,
  );
  const onUploadSuccess = useCallback(
    (task: { parentEntryId: string | null }) => {
      void Promise.all([
        mutations.queryClient.invalidateQueries({
          queryKey: orpc.projects.entries.list.queryKey({
            input: { projectId, parentEntryId: task.parentEntryId },
          }),
        }),
        mutations.queryClient.invalidateQueries({
          queryKey: orpc.projects.get.queryKey({ input: { projectId } }),
        }),
        mutations.queryClient.invalidateQueries({
          queryKey: orpc.projects.list.queryKey({ input: {} }),
        }),
      ]);
    },
    [mutations.queryClient, projectId],
  );
  const uploads = useProjectUploadQueue({
    projectId,
    onSuccess: onUploadSuccess,
  });
  const uploadLeaveGuard = useUnsavedChangesGuard(uploads.hasActiveTasks);

  const namiAttachments = useMemo<ChatInputAttachment[]>(
    () =>
      namiTaskIds.flatMap((taskId) => {
        const task = uploads.tasks.find((candidate) => candidate.id === taskId);
        if (!task) return [];
        const uploading =
          task.status === 'queued' ||
          task.status === 'uploading' ||
          task.status === 'finalizing';
        const progressPercent =
          task.totalBytes === 0
            ? 0
            : Math.round((task.loadedBytes / task.totalBytes) * 100);
        return [
          {
            documentId: task.entry?.id ?? task.id,
            fileName: task.file.name,
            uploading,
            progressPercent,
            errorMessage:
              task.status === 'failed' ? task.errorMessage : undefined,
          },
        ];
      }),
    [namiTaskIds, uploads.tasks],
  );
  const selectedIds = useMemo(
    () => [
      ...new Set([
        ...nav.selectedInputIds,
        ...namiAttachments
          .filter(
            (attachment) =>
              !attachment.uploading && attachment.errorMessage === undefined,
          )
          .map((attachment) => attachment.documentId),
      ]),
    ],
    [namiAttachments, nav.selectedInputIds],
  );
  const refreshChat = async () => {
    await Promise.all([
      mutations.queryClient.invalidateQueries({
        queryKey: orpc.projects.chat.history.queryKey({ input: { projectId } }),
      }),
      mutations.invalidateChangeset(),
      mutations.invalidateEntries(),
    ]);
  };
  const chat = useProjectChat(projectId, selectedIds, refreshChat);
  const messages = useMemo<Message[]>(() => {
    const history: Message[] = (data.chat.data ?? []).map((item) => ({
      id: item.id,
      role: item.role,
      content: item.content,
      timestamp: new Date(item.createdAt),
      dbMessageId: item.role === 'assistant' ? item.id : undefined,
    }));
    if (!chat.pendingUserMessage) return history;
    return [
      ...history,
      {
        id: 'pending-project-user-message',
        role: 'user',
        content: chat.pendingUserMessage,
        timestamp: new Date(),
        hasScreenshot: Boolean(chat.pendingScreenshotDataUrl),
        screenshotDataUrl: chat.pendingScreenshotDataUrl,
      },
    ];
  }, [chat.pendingScreenshotDataUrl, chat.pendingUserMessage, data.chat.data]);
  const onUpload = (files: FileList | null) => {
    uploads.enqueue({
      files: Array.from(files ?? []),
      parentEntryId: nav.currentFolder?.id ?? null,
      source: 'workspace',
    });
    if (uploadInput.current) uploadInput.current.value = '';
  };
  const onCreateFolder = () => {
    const name = window.prompt(
      gt('Folder name', { context: 'new project folder prompt' }),
    );
    if (name)
      mutations.createFolder.mutate({
        projectId,
        parentEntryId: nav.currentFolder?.id ?? null,
        name,
      });
  };
  const onNamiUpload = (files: FileList | null) => {
    const taskIds = uploads.enqueue({
      files: Array.from(files ?? []),
      parentEntryId: nav.currentFolder?.id ?? null,
      source: 'nami',
    });
    setNamiTaskIds((current) => [...current, ...taskIds]);
    if (namiUploadInput.current) namiUploadInput.current.value = '';
  };
  const findNamiTask = (identifier: string) =>
    uploads.tasks.find(
      (task) => task.id === identifier || task.entry?.id === identifier,
    );
  const currentChangeset = data.changeset.data;
  const publish = () => {
    if (currentChangeset)
      mutations.publish.mutate({ projectId, changesetId: currentChangeset.id });
  };
  const discard = () => {
    if (currentChangeset)
      mutations.discard.mutate({ projectId, changesetId: currentChangeset.id });
  };
  return {
    project: data.project,
    viewProps: {
      projectId,
      search: nav.search,
      setSearch: nav.setSearch,
      canCreate,
      canExecute,
      uploadInput,
      namiUploadInput,
      onUpload,
      onCreateFolder,
      onNamiUpload,
      namiAttachments,
      namiUploadsBlocked: namiAttachments.some(
        (attachment) => attachment.uploading || attachment.errorMessage,
      ),
      retryNamiAttachment: (identifier) => {
        const task = findNamiTask(identifier);
        if (task) uploads.retry(task.id);
      },
      removeNamiAttachment: (identifier) => {
        const task = findNamiTask(identifier);
        if (task) {
          uploads.cancel(task.id);
          setNamiTaskIds((current) =>
            current.filter((taskId) => taskId !== task.id),
          );
        }
      },
      selectedCount: selectedIds.length,
      entries: data.visibleEntries,
      selected: nav.selected,
      selectedInputIds: nav.selectedInputIds,
      stack: nav.folderStack,
      select: nav.setSelected,
      openFolder: nav.openFolder,
      openRoot: () => {
        nav.setFolderStack([]);
        nav.setSearch('');
      },
      openCrumb: (index) =>
        nav.setFolderStack((current) => current.slice(0, index + 1)),
      goBack: () => nav.setFolderStack((current) => current.slice(0, -1)),
      toggleInput: nav.toggleInput,
      changeset: currentChangeset,
      reviewOpen: mutations.reviewOpen,
      setReviewOpen: mutations.setReviewOpen,
      publish,
      discard,
      messages,
      streamingText: chat.streamingText,
      chatError: chat.error,
      activeToolCalls: chat.activeToolCalls,
      message: chat.message,
      chatBusy: chat.chatBusy,
      setMessage: chat.setMessage,
      send: (screenshot) => {
        setNamiTaskIds([]);
        void chat.send(screenshot);
      },
      cancel: chat.cancel,
      uploadTasks: uploads.tasks,
      cancelUpload: uploads.cancel,
      retryUpload: uploads.retry,
      uploadLeavePromptOpen: uploadLeaveGuard.isPrompting,
      confirmUploadLeave: () => {
        uploads.cancelAll();
        uploadLeaveGuard.confirmLeave();
      },
      cancelUploadLeave: uploadLeaveGuard.cancelLeave,
    },
  };
}
